export const FIELD_CATEGORIES = {
  SOCIAL_MEDIA: {
    CORE_ARTICLE: "Core Article Fields",
    GEOGRAPHIC_TEMPORAL: "Geographic & Temporal Fields",
    INFLUENCER_INFO: "Influencer/Author Information (Nested in Posts)",
    MEDIA_SOURCE: "Media Source Classification",
    LANGUAGE: "Language Detection",
    ENGAGEMENT: "Engagement Metrics",
    PLATFORM: "Platform-Specific Field",
  },
  INFLUENCER: {
    CONTAINER: "Container Structure",
    CORE_PROFILE: "Core Influencer Profile Fields",
    VISUAL_ASSETS: "Profile Visual Assets",
    AUDIENCE: "Audience Metrics",
    STATUS: "Account Status & Verification",
    PLATFORM_SPECIFIC: "Platform-Specific Fields",
    TEMPORAL: "Temporal Fields",
    GEOGRAPHIC: "Geographic & Interest Data",
    SYSTEM: "System & Metadata Fields",
  },
};

export const FIELD_DEFINITIONS = {
  // Core Article Fields
  article_obj_id: {
    type: "String",
    description: "Unique identifier for the social media post/article",
    example: '"68ff0bb3e189d57dc1a76966"',
    usage: "Primary key for tracking and referencing specific posts",
  },
  article_url: {
    type: "String (URL)",
    description: "Direct link to the original social media post",
    example: '"https://www.facebook.com/355272444328105_122226414020354398"',
    usage: "Navigate users to the source content; verify authenticity",
  },
  content: {
    type: "String",
    description: "The main text/body of the social media post",
    example: '"#Business360 | When it comes to electrified mobility..."',
    usage: "Display post content; perform text analysis; search functionality",
  },
  title: {
    type: "String",
    description: "Post title or headline (may be empty for some platforms)",
    example:
      '"" or "Trump\'s outrage toward Canada \'super childish behaviour\'"',
    usage: "Display as heading; search indexing",
  },
  word_count: {
    type: "Integer",
    description: "Total number of words in the post content",
    example: "50",
    usage: "Content length analysis; filtering by post size",
  },

  // Geographic & Temporal Fields
  country: {
    type: "String",
    description: "Country associated with the post (uppercase)",
    example: '"PHILIPPINES"',
    usage: "Geographic filtering; regional analytics; market segmentation",
  },
  date_created: {
    type: "String (ISO 8601 DateTime)",
    description: "When the record was created in the system",
    example: '"2025-10-27T06:05:39"',
    usage: "Track data ingestion; system auditing",
  },
  date_updated: {
    type: "String (ISO 8601 DateTime)",
    description: "Last update timestamp for the record",
    example: '"2025-10-27T06:57:26"',
    usage: "Data freshness indicator; sync management",
  },
  date_publish: {
    type: "String (ISO 8601 DateTime)",
    description:
      "When the post was originally published on the social platform",
    example: '"2025-10-27T05:45:52"',
    usage: "Chronological sorting; trend analysis; recency filtering",
  },

  // Influencer Fields
  social_poster_username: {
    type: "String",
    description: "Account username/handle",
    example: '"bncphl"',
    usage: "Display author; link to profile",
  },
  social_poster_identifier: {
    type: "String",
    description: "Unique platform-specific user ID",
    example: '"355272444328105"',
    usage: "Track unique users across posts",
  },
  social_poster_follower_count: {
    type: "Integer",
    description: "Number of followers/subscribers",
    example: "1600000",
    usage: "Influencer reach metrics; audience size analysis",
  },
  social_poster_verified: {
    type: "String (Boolean)",
    description: "Platform verification status",
    example: '"False" or "True"',
    usage: "Credibility indicator; filter verified accounts",
  },
  social_poster_profile_url: {
    type: "String (URL)",
    description: "Link to the user's profile page",
    example: '"https://www.facebook.com/bncphl"',
    usage: "Navigate to author's profile",
  },
  social_poster_engagement_rate: {
    type: "Float",
    description: "Engagement rate metric (interactions/followers)",
    example: "0.0000013455931823278762",
    usage: "Influencer effectiveness analysis; performance metrics",
  },
  social_poster_account_status: {
    type: "String",
    description: "Current status of the account",
    example: '"ACTIVE"',
    usage: "Filter active accounts; data quality checks",
  },

  // Media Source
  media_source_name: {
    type: "String",
    description: "Category of media source",
    example: '"Social Media"',
    usage: "Content categorization",
  },
  media_source_type_flag: {
    type: "String",
    description: "Delivery type indicator",
    example: '"online"',
    usage: "Channel classification",
  },

  // Language
  languageCode: {
    type: "String",
    description: "ISO language code",
    example: '"en", "tl", "es"',
    usage: "Language filtering; localization",
  },
  languageName: {
    type: "String",
    description: "Full language name (uppercase)",
    example: '"ENGLISH", "TAGALOG"',
    usage: "Display language name",
  },
  languagePercentInContent: {
    type: "Integer",
    description: "Percentage of content in this language",
    example: "99",
    usage: "Multi-language content analysis; primary language detection",
  },

  // Engagement Metrics
  soc_like_count: {
    type: "Integer",
    description: "Number of likes/reactions",
    example: "3",
    usage: "Popular content identification; engagement tracking",
  },
  soc_comment_count: {
    type: "Integer",
    description: "Number of comments",
    example: "0",
    usage: "Discussion level indicator; engagement depth",
  },
  soc_share_count: {
    type: "Integer",
    description: "Number of shares/retweets",
    example: "0",
    usage: "Virality metric; content reach",
  },
  soc_view_count: {
    type: "Integer",
    description: "Number of views (platform-dependent)",
    example: "6626",
    usage: "Reach metrics; visibility analysis",
  },
  soc_te_score: {
    type: "Integer",
    description: "Sum of all engagement actions",
    example: "240",
    calculation: "likes + comments + shares + other reactions",
    usage: "Overall engagement ranking; performance comparison",
  },
  soc_eng_score: {
    type: "Float",
    description: "Weighted engagement metric",
    example: "229",
    usage: "Normalized engagement comparison; performance scoring",
  },
  soc_mm_score: {
    type: "Float",
    description: "Proprietary metric for content momentum",
    example: "228",
    usage: "Trending content identification",
  },
  soc_er_score: {
    type: "Integer",
    description: "Engagement rate calculation",
    example: "0",
    usage: "Efficiency of engagement relative to reach",
  },

  // Platform
  soc_source: {
    type: "String",
    description: "Social media platform identifier",
    example: '"facebook", "twitter", "youtube", "reddit", "tiktok"',
    usage:
      "Platform filtering; cross-platform analysis; platform-specific rendering",
  },

  // Container Structure
  rowIndex: {
    type: "Integer",
    description: "Position index in the result set",
    example: "0",
    usage: "Pagination; result ordering",
  },
  links: {
    type: "Array of Strings (URLs)",
    description: "Collection of all profile URLs for the influencer",
    example: '["https://www.facebook.com/bncphl"]',
    usage: "Quick access to all profile links; cross-platform verification",
  },
  data: {
    type: "Array of Objects",
    description: "Contains the complete influencer profile information",
    usage: "Access influencer profile details",
    note: "Usually contains one object per influencer",
  },

  // Core Influencer Profile
  _id: {
    type: "String (ObjectId)",
    description: "MongoDB unique identifier for the influencer record",
    example: '"6780adde6ddabed2c10c6592"',
    usage: "Database primary key; record tracking",
  },
  social_poster_name: {
    type: "String",
    description: "Full display name of the influencer/page",
    example: '"Bilyonaryo News Channel"',
    usage: "Display prominent name; search functionality",
  },
  social_poster_source: {
    type: "String",
    description: "Social media platform where this profile exists",
    example:
      '"facebook", "instagram", "twitter", "tiktok", "youtube", "reddit"',
    usage: "Platform filtering; icon display; platform-specific features",
  },
  social_poster_type: {
    type: "String",
    description: "Type of social media account",
    example: '"FacebookPage", "TikTokUser", "RedditUser"',
    usage: "Account categorization; different handling for pages vs. users",
  },

  // Visual Assets
  social_poster_profile_img: {
    type: "String (URL)",
    description: "URL to the profile picture/avatar",
    example:
      '"https://d18uvbkdlzzwn7.cloudfront.net/fb/64/a6/00/64a60030fada..."',
    usage: "Display profile image; user identification",
    note: "May be CDN URL with expiration parameters",
  },
  social_poster_profile_banner: {
    type: "String (URL)",
    description: "URL to the profile banner/cover image",
    example: '"" (often empty)',
    usage: "Display cover photo; enhanced profile presentation",
  },
  social_poster_profile: {
    type: "String",
    description: "Profile bio or description text",
    example: '"" or "Biometrics" or "officer operations @ vfs global Riyadh"',
    usage: "Display bio; context about the account",
  },

  // Audience Metrics
  social_poster_following_count: {
    type: "Integer",
    description: "Number of accounts this profile follows",
    example: "0 (pages often follow 0) or 9730",
    usage: "Account behavior analysis; authenticity checks",
  },
  social_poster_likes_count: {
    type: "Integer",
    description:
      "Total likes received across all content (Facebook) or page likes",
    example: "635296",
    usage: "Overall popularity metric; engagement history",
  },
  social_poster_content_count: {
    type: "Integer",
    description: "Total number of posts/videos published",
    example: "0 or 935",
    usage: "Content productivity; posting frequency analysis",
  },
  social_poster_views_count: {
    type: "Integer",
    description: "Total views across all content",
    example: "0 (often 0 for non-video platforms)",
    usage: "Video content reach; visibility metrics",
  },

  // Status & Verification
  social_poster_business: {
    type: "Boolean or null",
    description: "Business/company affiliation",
    example: "null or business name",
    usage: "Business account identification; categorization",
  },

  // Temporal
  social_poster_started_date: {
    type: "String (DateTime) or null",
    description: "Account creation/start date",
    example: 'null or "2019-03-31 05:34:26"',
    usage: "Account age calculation; authenticity verification",
  },
  social_poster_latest_post: {
    type: "String (DateTime)",
    description: "Timestamp of most recent post",
    example: '"" or DateTime string',
    usage: "Activity status; posting frequency analysis",
  },
  social_poster_scrape_datetime: {
    type: "String (DateTime) or null",
    description: "When the profile data was last scraped",
    example: "null",
    usage: "Data freshness indicator; update scheduling",
  },

  // Geographic
  social_poster_country: {
    type: "String or null",
    description: "Country/region of the account (uppercase)",
    example: '"PHILIPPINES" or null',
    usage: "Geographic filtering; regional analytics; market segmentation",
  },
  social_poster_interests: {
    type: "Array of Strings",
    description: "User-defined interests or categories",
    example: "[] (usually empty)",
    usage: "Content categorization; audience targeting",
  },

  // System & Metadata
  social_poster_processing_machine: {
    type: "String or null",
    description: "System identifier for data processing",
    example: '"" or null',
    usage: "Internal tracking; debugging; data pipeline management",
  },
  social_poster_user_id: {
    type: "String or null",
    description: "Alternative user identifier",
    example: "null",
    usage: "Cross-reference; legacy system compatibility",
  },
  scraped_from: {
    type: "String",
    description: "Source of the scraped data",
    example: '""',
    usage: "Data provenance tracking",
  },
  data_status: {
    type: "String",
    description: "Data lifecycle status",
    example: '"production"',
    possibleValues: '"production", "staging", "archived"',
    usage: "Environment filtering; data quality control",
  },
  created_by_id: {
    type: "String (ObjectId)",
    description: "ID of the user/system that created the record",
    example: '"64000889392a74bef6a38c77"',
    usage: "Audit trail; access control",
  },
};

export const PLATFORM_REACTIONS = [
  { code: "soc_love_count", label: "Love reactions (Facebook)" },
  { code: "soc_wow_count", label: "Wow reactions (Facebook)" },
  { code: "soc_haha_count", label: "Haha reactions (Facebook)" },
  { code: "soc_sad_count", label: "Sad reactions (Facebook)" },
  { code: "soc_angry_count", label: "Angry reactions (Facebook)" },
  { code: "soc_save_count", label: "Save/bookmark count" },
  { code: "soc_dislike_count", label: "Dislikes (YouTube)" },
];

export const PLATFORM_SPECIFIC_FIELDS = {
  pinterest: [
    {
      code: "social_poster_board_count",
      label: "Number of boards created",
      type: "Integer",
    },
    {
      code: "social_poster_pin_count",
      label: "Number of pins created",
      type: "Integer",
    },
  ],
  twitter: [
    {
      code: "social_poster_list_count",
      label: "Number of lists",
      type: "Integer",
    },
  ],
};
